<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  wizardData: { type: Object, required: true },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const labels = useMapGetter('labels/getLabels');
const labelOptions = computed(() =>
  labels.value.map(label => ({ value: label.id, label: label.title }))
);

const isLabel = computed(() => props.wizardData.audienceType === 'label');

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const contactCount = computed(() => {
  if (isLabel.value) {
    return t('CAMPAIGN.WHATSAPP.WIZARD.CONFIRM.LABEL_COUNT', {
      count: props.wizardData.selectedAudience.length,
    });
  }
  return t('CAMPAIGN.WHATSAPP.WIZARD.CONFIRM.CSV_COUNT', {
    count: props.wizardData.csvTotalRows,
  });
});

const summaryItems = computed(() => [
  {
    icon: 'i-lucide-type',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL'),
    value: props.wizardData.title,
  },
  {
    icon: 'i-lucide-file-text',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL'),
    value: props.wizardData.selectedTemplate?.name || '-',
  },
  {
    icon: 'i-lucide-users',
    label: t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.TITLE'),
    value: contactCount.value,
  },
  {
    icon: 'i-lucide-send',
    label: t('CAMPAIGN.WHATSAPP.WIZARD.CONFIRM.TYPE'),
    value: isLabel.value
      ? t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.BY_LABELS')
      : t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.BY_CSV'),
  },
]);

const isScheduledForLater = computed(
  () =>
    props.wizardData.scheduledAt &&
    props.wizardData.scheduledAt > currentDateTime.value
);

const warningMessage = computed(() => {
  if (isScheduledForLater.value) {
    return t('CAMPAIGN.WHATSAPP.WIZARD.CONFIRM.WARNING_SCHEDULED');
  }
  return t('CAMPAIGN.WHATSAPP.WIZARD.CONFIRM.WARNING');
});

const updateScheduledAt = value => {
  emit('update', { scheduledAt: value });
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.CONFIRM.TITLE') }}
    </h3>
    <p class="text-sm text-n-slate-11">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.CONFIRM.DESCRIPTION') }}
    </p>

    <div class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-2">
      <div
        v-for="item in summaryItems"
        :key="item.label"
        class="flex items-center gap-3"
      >
        <span class="size-4 text-n-slate-10 shrink-0" :class="[item.icon]" />
        <span class="text-sm text-n-slate-11 w-32 shrink-0">
          {{ item.label }}
        </span>
        <span class="text-sm font-medium text-n-slate-12">
          {{ item.value }}
        </span>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <div class="flex items-center gap-1.5">
        <label
          for="conversation-label"
          class="text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONVERSATION_LABEL.LABEL') }}
        </label>
        <button
          v-tooltip.top="{
            content: t(
              'CAMPAIGN.WHATSAPP.CREATE.FORM.CONVERSATION_LABEL.TOOLTIP'
            ),
            container: false,
            popperClass: 'max-w-xs',
          }"
          type="button"
          :aria-label="
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONVERSATION_LABEL.TOOLTIP_LABEL')
          "
          class="inline-flex items-center justify-center !p-0 rounded size-5 shrink-0 cursor-help text-n-slate-11 hover:text-n-slate-12 focus-visible:outline-n-brand"
        >
          <span class="i-lucide-info size-4" aria-hidden="true" />
        </button>
      </div>
      <ComboBox
        id="conversation-label"
        :model-value="wizardData.conversationLabelId"
        :options="labelOptions"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONVERSATION_LABEL.PLACEHOLDER')
        "
        @update:model-value="
          emit('update', { conversationLabelId: $event || null })
        "
      />
      <p class="mt-1 mb-0 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONVERSATION_LABEL.HELP') }}
      </p>
    </div>

    <!-- Scheduled At -->
    <Input
      :model-value="wizardData.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      @update:model-value="updateScheduledAt"
    />

    <div
      class="flex items-center gap-2 p-3 rounded-lg bg-n-amber-2 text-n-amber-11"
    >
      <span class="i-lucide-alert-triangle size-4 shrink-0" />
      <span class="text-xs">
        {{ warningMessage }}
      </span>
    </div>
  </div>
</template>
